import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, TextField, InputAdornment,
} from '@mui/material';
import StoreLocationIcon from '@mui/icons-material/StoreMallDirectory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LabelIcon from '@mui/icons-material/Label';

import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const Field = ({ label, name, value, onChange, placeholder, type = 'text',
  required, align, startIcon, maxLength }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small" name={name} value={value ?? ''} onChange={onChange}
      placeholder={placeholder || label} type={type} required={required}
      inputProps={{ maxLength, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      InputProps={startIcon ? { startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment> } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px', bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

// ── Section card ──────────────────────────────────────────────────────────────
const Section = ({ title, icon, children, color = C.accent }) => (
  <Box sx={{
    bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
    border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
      bgcolor: `${color}0d`,
    }}>
      <Box sx={{ color, fontSize: 15, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      {children}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const StoreLocationCreate = () => {
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); }
  }, [isAuthenticated, token, logout]);

  const [formData, setFormData] = useState({
    CompanyId: companyId,
    Name: '',
    CreatedBy: userId,
    CreatedDate: new Date().toISOString(),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.Name.trim()) { setError('Name is required.'); return; }

    setSaving(true);
    try {
      const existing = await fetchData('StoreLocationApi', `/${companyId}`, token);
      if (existing.response_data?.some(item => item.Name === formData.Name)) {
        setError('A StoreLocation with this name already exists.'); setSaving(false); return;
      }
      await postData('StoreLocationApi', formData, token);
      history.push('/StoreLocationIndex');
    } catch (err) {
      console.error('Error creating StoreLocation:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <StoreLocationIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            StoreLocation Create
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button component={Link} to="/StoreLocationIndex" size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 600, textTransform: 'none',
              color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
            }}>
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={saving} size="small"
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 700, textTransform: 'none',
              bgcolor: C.accent, color: '#fff', borderRadius: '7px',
              px: 1.4, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDark },
              '&:disabled': { bgcolor: C.accentBorder },
            }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 1, px: '10px', py: '6px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px' }}>
          <Typography sx={{ fontSize: '11px', color: C.danger }}>{error}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          {/* ── Basic Info ── */}
          <Section title="StoreLocation Information" icon={<LabelIcon />}>
            <Box sx={{ flex: '1 1 260px' }}>
              <Field label="Name" name="Name" value={formData.Name}
                onChange={handleChange} required maxLength={100} />
            </Box>
          </Section>

        </Box>
      </form>
    </Box>
  );
};

export default StoreLocationCreate;
